import type { LectureTreeNode } from '../types/Lecture';

export type LectureTreeNodesById = Record<string, LectureTreeNode>;

export type LectureTreeChildrenMap = Map<string | null, LectureTreeNode[]>;

/** Deepest folder level the backend accepts (root-level nodes are depth 1). */
export const MAX_LECTURE_TREE_DEPTH = 5;

export interface FolderOption {
  id: string;
  name: string;
  depth: number;
  indent: number;
  label: string;
}

export type FolderCheckState = 'checked' | 'indeterminate' | 'unchecked';

type ExpandedIds = ReadonlySet<string> | readonly string[];

const asExpandedSet = (expandedIds: ExpandedIds): ReadonlySet<string> =>
  expandedIds instanceof Set ? expandedIds : new Set(expandedIds);

/** Build parent-to-children buckets while preserving the input object's order. */
export const buildChildrenMap = (
  nodesById: LectureTreeNodesById
): LectureTreeChildrenMap => {
  const children: LectureTreeChildrenMap = new Map();

  Object.values(nodesById).forEach((node) => {
    const siblings = children.get(node.parentId) ?? [];
    siblings.push(node);
    children.set(node.parentId, siblings);
  });

  return children;
};

export const descendantDocumentIds = (
  id: string,
  nodesById: LectureTreeNodesById,
  {
    includeHidden = false,
    childrenMap,
  }: {
    includeHidden?: boolean;
    /** Prebuilt children buckets, so callers can avoid an O(N) rebuild per folder. */
    childrenMap?: LectureTreeChildrenMap;
  } = {}
): string[] => {
  const root = nodesById[id];
  if (!root || root.nodeType !== 'FOLDER') return [];
  if (!includeHidden && root.effectiveHidden) return [];

  const children = childrenMap ?? buildChildrenMap(nodesById);
  const documentIds: string[] = [];

  const visit = (parentId: string) => {
    (children.get(parentId) ?? []).forEach((node) => {
      if (!includeHidden && node.effectiveHidden) return;

      if (node.nodeType === 'DOCUMENT') {
        documentIds.push(node.id);
      } else {
        visit(node.id);
      }
    });
  };

  visit(id);
  return documentIds;
};

export const folderCheckState = (
  id: string,
  nodesById: LectureTreeNodesById,
  selectedIdSet: ReadonlySet<string>,
  childrenMap?: LectureTreeChildrenMap
): FolderCheckState => {
  const documentIds = descendantDocumentIds(id, nodesById, { childrenMap });
  if (documentIds.length === 0) return 'unchecked';

  const selectedCount = documentIds.reduce(
    (count, documentId) => count + Number(selectedIdSet.has(documentId)),
    0
  );

  if (selectedCount === 0) return 'unchecked';
  if (selectedCount === documentIds.length) return 'checked';
  return 'indeterminate';
};

/**
 * The folder whose non-hidden descendant documents are EXACTLY the given
 * selection, or null when no folder matches.
 *
 * Nested folders can share one document set (a folder holding nothing but one
 * subfolder), so the deepest match — the tightest fit — wins.
 */
const folderMatchingSelection = (
  selectedDocumentIds: readonly string[],
  nodesById: LectureTreeNodesById,
  childrenMap?: LectureTreeChildrenMap
): LectureTreeNode | null => {
  if (selectedDocumentIds.length === 0) return null;

  const selectedIds = new Set(selectedDocumentIds);
  const children = childrenMap ?? buildChildrenMap(nodesById);
  let match: LectureTreeNode | null = null;

  Object.values(nodesById).forEach((node) => {
    if (node.nodeType !== 'FOLDER') return;
    if (match && node.depth <= match.depth) return;

    const documentIds = descendantDocumentIds(node.id, nodesById, {
      childrenMap: children,
    });

    if (documentIds.length !== selectedIds.size) return;
    if (!documentIds.every((id) => selectedIds.has(id))) return;
    match = node;
  });

  return match;
};

/**
 * The folder the preview panel aggregates, if any: the one folder whose
 * documents are exactly the selection (of two or more documents). Its name
 * titles the panel; its id carries a course day's recap (summary and
 * episode).
 */
export const selectionPreviewFolder = (
  selectedDocumentIds: readonly string[],
  nodesById: LectureTreeNodesById,
  childrenMap?: LectureTreeChildrenMap
): LectureTreeNode | undefined => {
  if (selectedDocumentIds.length < 2) return undefined;

  return (
    folderMatchingSelection(selectedDocumentIds, nodesById, childrenMap) ??
    undefined
  );
};

/**
 * Heading override for the preview panel, which is driven by the selection
 * alone (ORA-498): a selection that is exactly one folder's document set is
 * titled with that folder's name.
 *
 * Returns undefined when there is nothing to override with — no selection, a
 * single document (the panel titles that with the document itself), or a
 * selection no single folder accounts for (the panel falls back to its
 * "N Lectures Selected" heading).
 */
export const selectionPreviewTitle = (
  selectedDocumentIds: readonly string[],
  nodesById: LectureTreeNodesById,
  childrenMap?: LectureTreeChildrenMap
): string | undefined =>
  selectionPreviewFolder(selectedDocumentIds, nodesById, childrenMap)?.name;

/**
 * Return rendered rows in the server's depth-first order. A row is omitted
 * when it is effectively hidden or has any collapsed folder ancestor.
 */
export const visibleRows = (
  orderedIds: readonly string[],
  nodesById: LectureTreeNodesById,
  expandedIds: ExpandedIds,
  showHidden: boolean
): LectureTreeNode[] => {
  const expanded = asExpandedSet(expandedIds);

  return orderedIds.flatMap((id) => {
    const node = nodesById[id];
    if (!node || (!showHidden && node.effectiveHidden)) return [];

    let parentId = node.parentId;
    const visited = new Set<string>();

    while (parentId) {
      if (visited.has(parentId)) return [];
      visited.add(parentId);

      const parent = nodesById[parentId];
      if (!parent) break;
      if (!expanded.has(parent.id)) return [];
      if (!showHidden && parent.effectiveHidden) return [];
      parentId = parent.parentId;
    }

    return [node];
  });
};

/** Folder destinations in server tree order, with root-relative indentation. */
export const folderOptions = (
  orderedIds: readonly string[],
  nodesById: LectureTreeNodesById
): FolderOption[] =>
  orderedIds.flatMap((id) => {
    const node = nodesById[id];
    if (!node || node.nodeType !== 'FOLDER') return [];

    const indent = Math.max(0, node.depth - 1);
    return [
      {
        id: node.id,
        name: node.name,
        depth: node.depth,
        indent,
        label: `${'  '.repeat(indent)}${node.name}`,
      },
    ];
  });

/** True when nodeId is strictly below potentialAncestorId in the tree. */
export const isDescendant = (
  nodeId: string,
  potentialAncestorId: string,
  nodesById: LectureTreeNodesById
): boolean => {
  if (nodeId === potentialAncestorId) return false;

  let parentId = nodesById[nodeId]?.parentId;
  const visited = new Set<string>();

  while (parentId) {
    if (parentId === potentialAncestorId) return true;
    if (visited.has(parentId)) return false;
    visited.add(parentId);
    parentId = nodesById[parentId]?.parentId;
  }

  return false;
};

/**
 * Nearest ancestor (walking up parentId) that has its OWN `date` set — the
 * source of a node's `effectiveDate` when the node has none of its own.
 * Returns null when no ancestor has a date (or the node has no ancestors).
 */
export const nearestDatedAncestor = (
  nodeId: string,
  nodesById: LectureTreeNodesById
): LectureTreeNode | null => {
  let parentId = nodesById[nodeId]?.parentId;
  const visited = new Set<string>();

  while (parentId) {
    const parent = nodesById[parentId];
    if (!parent) return null;
    if (parent.date) return parent;
    if (visited.has(parentId)) return null;
    visited.add(parentId);
    parentId = parent.parentId;
  }

  return null;
};

/**
 * The node as it looks immediately after its OWN date is set or cleared, for
 * the optimistic update that lands before the authoritative refresh.
 *
 * Clearing a date does not clear the row's date: the node falls back to the
 * date it INHERITS from its nearest dated ancestor (none, when there is no
 * such ancestor). Reusing the previous `effectiveDate` would leave the row
 * showing the date the user just cleared — for good, if the refresh fails.
 */
export const nodeAfterDateChange = (
  node: LectureTreeNode,
  date: string | null,
  nodesById: LectureTreeNodesById
): LectureTreeNode => ({
  ...node,
  date,
  effectiveDate: date ?? nearestDatedAncestor(node.id, nodesById)?.date ?? null,
});

/**
 * Number of levels the node occupies, itself included: a document (or empty
 * folder) is 1, a folder holding documents is 2, and so on. Moving a node under
 * a destination of depth D would push its deepest descendant to D + height.
 */
export const subtreeHeight = (
  id: string,
  nodesById: LectureTreeNodesById,
  childrenMap?: LectureTreeChildrenMap
): number => {
  if (!nodesById[id]) return 1;
  const children = childrenMap ?? buildChildrenMap(nodesById);
  const visited = new Set<string>();

  const measure = (nodeId: string): number => {
    if (visited.has(nodeId)) return 1;
    visited.add(nodeId);

    return (children.get(nodeId) ?? []).reduce(
      (height, child) => Math.max(height, 1 + measure(child.id)),
      1
    );
  };

  return measure(id);
};

/**
 * True when a subtree of `height` fits under a destination at `destinationDepth`
 * (the destination folder's own depth, or 0 for the exam root).
 */
export const fitsWithinLectureTreeDepth = (
  destinationDepth: number,
  height: number
): boolean => destinationDepth + height <= MAX_LECTURE_TREE_DEPTH;

/**
 * Folder ids a destination picker must refuse: a hidden folder (which would
 * swallow whatever lands in it and quietly drop it out of the persisted
 * selection, ORA-498 T2) and a folder with no room left under the depth cap.
 *
 * `movingNode` is the node being MOVED, when there is one: its own id and its
 * whole subtree are refused too, and the depth check uses that subtree's
 * height. Omit it for an upload, which only ever places a single new document.
 * One implementation so the move modal and the upload picker cannot drift —
 * offering a destination the mapping then rejects with MAX_DEPTH_EXCEEDED, or
 * one that makes the upload land invisibly, is the failure this prevents.
 */
export const disabledFolderDestinationIds = (
  nodes: readonly LectureTreeNode[],
  movingNode?: LectureTreeNode | null
): string[] => {
  const nodesById: LectureTreeNodesById = Object.fromEntries(
    nodes.map((node) => [node.id, node])
  );

  const childrenMap = buildChildrenMap(nodesById);

  const height = movingNode
    ? subtreeHeight(movingNode.id, nodesById, childrenMap)
    : 1;

  return nodes
    .filter(
      (candidate) =>
        candidate.nodeType === 'FOLDER' &&
        ((movingNode?.nodeType === 'FOLDER' &&
          (candidate.id === movingNode.id ||
            isDescendant(candidate.id, movingNode.id, nodesById))) ||
          !fitsWithinLectureTreeDepth(candidate.depth, height) ||
          candidate.effectiveHidden)
    )
    .map((candidate) => candidate.id);
};

/**
 * Folders `node` could be moved into, plus the exam root, excluding wherever it
 * already sits. Mirrors the move modal's own exclusions: a folder can't go into
 * itself or its own subtree, nothing can go somewhere too deep to hold it, and
 * a hidden destination would hide what lands in it (ORA-498 T2).
 */
export const moveDestinationIds = (
  node: LectureTreeNode,
  nodes: LectureTreeNode[],
  nodesById: LectureTreeNodesById
): (string | null)[] => {
  const childrenMap = buildChildrenMap(nodesById);
  const height = subtreeHeight(node.id, nodesById, childrenMap);
  const currentParentId = node.parentId ?? null;

  const folderIds = nodes
    .filter(
      (candidate) =>
        candidate.nodeType === 'FOLDER' &&
        !(
          node.nodeType === 'FOLDER' &&
          (candidate.id === node.id ||
            isDescendant(candidate.id, node.id, nodesById))
        ) &&
        fitsWithinLectureTreeDepth(candidate.depth, height) &&
        !candidate.effectiveHidden
    )
    .map((candidate) => candidate.id);

  // The exam root is always a destination, so it only counts when the node is
  // not already there.
  return [null, ...folderIds].filter((id) => id !== currentParentId);
};

/**
 * True when there is anywhere to move `node` — i.e. somewhere other than where
 * it already is. False hides the move action instead of opening a picker whose
 * only entry is the node's own parent.
 */
export const canMoveLectureTreeNode = (
  node: LectureTreeNode,
  nodes: LectureTreeNode[],
  nodesById: LectureTreeNodesById
): boolean => moveDestinationIds(node, nodes, nodesById).length > 0;

/** Depth of the destination a node would be appended into (root = 0). */
export const destinationDepth = (
  parentId: string | null,
  nodesById: LectureTreeNodesById
): number => (parentId ? (nodesById[parentId]?.depth ?? 0) : 0);

/**
 * Selection after hiding `node`, or null when nothing changes. A hidden folder
 * hides its whole subtree server-side, so every descendant document (hidden
 * ones included) has to leave the selection; a hidden document drops itself.
 */
export const selectionAfterHiding = (
  node: LectureTreeNode,
  nodesById: LectureTreeNodesById,
  selectedDocumentIds: readonly string[]
): string[] | null => {
  const hiddenDocumentIds = new Set(
    node.nodeType === 'FOLDER'
      ? descendantDocumentIds(node.id, nodesById, { includeHidden: true })
      : [node.id]
  );

  const remainingIds = selectedDocumentIds.filter(
    (id) => !hiddenDocumentIds.has(id)
  );

  return remainingIds.length === selectedDocumentIds.length
    ? null
    : remainingIds;
};
